import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store";
import UserInfoForm from "./UserInfo/UserInfoForm";
import ReauthenticateModal from "./UserInfo/ReauthenticateModal";
import AllPassesTable from "./UserInfo/AllPassesTable";
import LessonsTable from "./UserInfo/LessonsTable";
import { formatPln, formatLessonStart } from "../utils/paymentFormat";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 800 }, // Increase the width for both xs and sm breakpoints
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const UserModal = ({ open, handleClose, user, showAlert }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [reauthenticateOpen, setReauthenticateOpen] = useState(false);
  const {
    lessons,
    setUserDetails,
    setLessons,
    passes,
    setPasses,
    pendingSignups,
    fetchPendingSignups,
  } = useAuthStore((state) => ({
    lessons: state.lessons,
    setUserDetails: state.setUserDetails,
    setLessons: state.setLessons,
    passes: state.passes,
    setPasses: state.setPasses,
    pendingSignups: state.pendingSignups,
    fetchPendingSignups: state.fetchPendingSignups,
  }));

  useEffect(() => {
    if (user) {
      setUserDetails({
        email: user.email,
        name: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
      });
    }
  }, [user, setUserDetails]);

  useEffect(() => {
    const fetchLessons = async () => {
      if (!user) {
        return;
      }
      const idToken = await user.getIdToken();
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/user/me/lessons`,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );
        const data = await response.json();
        if (Array.isArray(data.objects)) {
          setLessons(data.objects);
        } else {
          console.error("Invalid lessons data format", data);
          setLessons([]);
        }
      } catch (error) {
        console.error("Failed to fetch lessons", error);
        setLessons([]);
      }
    };

    fetchLessons();
  }, [user, setLessons]);

  useEffect(() => {
    const fetchPasses = async () => {
      if (!user) {
        return;
      }
      const idToken = await user.getIdToken();
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/pass/mine?active=true`,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );
        const data = await response.json();
        if (Array.isArray(data.objects)) {
          setPasses(data.objects);
        } else {
          console.error("Invalid passes data format", data);
          setPasses([]);
        }
      } catch (error) {
        console.error("Failed to fetch passes", error);
        setPasses([]);
      }
    };

    fetchPasses();
  }, [user, setPasses]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Refresh the owed summary whenever the modal opens so the "Płatności" tab
  // (and the account-icon badge it explains) reflects the latest state.
  useEffect(() => {
    if (open) {
      fetchPendingSignups();
    }
  }, [open, fetchPendingSignups]);

  // Lessons the user still owes for: past unpaid online "debt" + cash-on-site.
  // Mirrors the account badge (due online + due on-site), so the tab always
  // explains exactly what the red number stands for.
  const nowTs = Date.now();
  const isPastOrInvalid = (l) => {
    const start = l.lesson_start ? new Date(l.lesson_start).getTime() : NaN;
    return Number.isNaN(start) || start <= nowTs;
  };
  const onlineOwed = (pendingSignups?.online?.lessons || []).filter(
    (l) => isPastOrInvalid(l) || l.pass_renewable === true
  );
  const onSiteOwed = (pendingSignups?.on_site?.lessons || []).filter(
    isPastOrInvalid
  );
  const onlineTotal = onlineOwed.reduce((s, l) => s + (l.price || 0), 0);
  const onSiteTotal = onSiteOwed.reduce((s, l) => s + (l.price || 0), 0);
  const owedCount = onlineOwed.length + onSiteOwed.length;

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
        }}
        aria-labelledby="user-modal-title"
        aria-describedby="user-modal-description"
      >
        <Box sx={modalStyle}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Moje zajęcia" />
            <Tab label="Dostępne karnety" />
            <Tab label="Płatności" />
            <Tab label="Moje dane" />
          </Tabs>
          {tabIndex === 0 && (
            <Box
              sx={{
                maxHeight: { xs: "60vh", sm: "70vh" },
                overflowY: "auto",
                mt: 2,
                pr: 1,
              }}
            >
              <LessonsTable lessons={lessons} />
            </Box>
          )}
          {tabIndex === 1 && (
            <Box
              sx={{
                maxHeight: { xs: "60vh", sm: "70vh" },
                overflowY: "auto",
                mt: 2,
                pr: 1,
              }}
            >
              <AllPassesTable />
            </Box>
          )}
          {tabIndex === 2 && (
            <Box
              sx={{
                maxHeight: { xs: "60vh", sm: "70vh" },
                overflowY: "auto",
                mt: 2,
                pr: 1,
              }}
            >
              {owedCount === 0 ? (
                <Typography sx={{ py: 2 }}>
                  Nie masz żadnych zaległych płatności. 🎉
                </Typography>
              ) : (
                <>
                  {onlineOwed.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Do opłacenia online ({onlineOwed.length}) —{" "}
                        {formatPln(onlineTotal)}
                      </Typography>
                      {onlineOwed.map((l) => (
                        <Box
                          key={`online-${l.lesson_id}`}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 0.75,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <span>
                            {l.lesson_name}
                            <br />
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatLessonStart(l.lesson_start)}
                            </Typography>
                          </span>
                          <strong>{formatPln(l.price)}</strong>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {onSiteOwed.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Do zapłaty na miejscu ({onSiteOwed.length}) —{" "}
                        {formatPln(onSiteTotal)}
                      </Typography>
                      {onSiteOwed.map((l) => (
                        <Box
                          key={`onsite-${l.lesson_id}`}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 0.75,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <span>
                            {l.lesson_name}
                            <br />
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatLessonStart(l.lesson_start)}
                            </Typography>
                          </span>
                          <strong>{formatPln(l.price)}</strong>
                        </Box>
                      ))}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Te zajęcia rozliczasz gotówką na miejscu.
                      </Typography>
                    </Box>
                  )}
                  {onlineOwed.length > 0 && (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => {
                        handleClose();
                        navigate("/moje-platnosci");
                      }}
                    >
                      Przejdź do płatności
                    </Button>
                  )}
                </>
              )}
            </Box>
          )}
          {tabIndex === 3 && (
            <Box>
              <UserInfoForm
                user={user}
                showAlert={showAlert}
                handleClose={handleClose}
                setReauthenticateOpen={setReauthenticateOpen}
              />
            </Box>
          )}
        </Box>
      </Modal>

      <ReauthenticateModal
        open={reauthenticateOpen}
        handleClose={() => setReauthenticateOpen(false)}
        showAlert={showAlert}
        handleCloseMainModal={handleClose}
      />
    </>
  );
};

export default UserModal;
